import { Field, Input, Select } from "@aigntx/ui";
import type { FormColumn, FormField } from "../types";

export type FormValues = Record<string, unknown>;

export interface LineItem {
  description: string;
  amount: number | "";
}

function asLines(value: unknown): LineItem[] {
  return Array.isArray(value) ? (value as LineItem[]) : [];
}

export type TableRowValue = Record<string, string | number>;

function asRows(value: unknown): TableRowValue[] {
  return Array.isArray(value) ? (value as TableRowValue[]) : [];
}

const blankRow = (columns: FormColumn[]): TableRowValue =>
  Object.fromEntries(columns.map((c) => [c.key, ""]));

/** Columns whose totals are worth having — a labour count nobody can add up is
 *  the reason these were tables on paper in the first place. */
const totalsFor = (columns: FormColumn[], rows: TableRowValue[]) =>
  columns
    .filter((c) => c.type === "number")
    .map((c) => ({
      label: c.label,
      total: rows.reduce((sum, r) => sum + (Number(r[c.key]) || 0), 0),
    }))
    .filter((t) => t.total > 0);

/**
 * Renders one field from the form catalog. Every form in the system is drawn by
 * this one component, which is why adding a form is a backend config entry and
 * nothing more.
 */
export function FormFieldInput({
  field,
  value,
  onChange,
}: {
  field: FormField;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const label = field.required ? `${field.label} *` : field.label;
  const cls = field.span > 1 ? "ff ff--wide" : "ff";

  if (field.type === "bool") {
    return (
      <div className={cls}>
        <Field label={label} hint={field.hint}>
          <div className="segmented">
            <button type="button" className={value === true ? "seg is-on" : "seg"} onClick={() => onChange(true)}>
              Yes
            </button>
            <button type="button" className={value === false ? "seg is-on" : "seg"} onClick={() => onChange(false)}>
              No
            </button>
          </div>
        </Field>
      </div>
    );
  }

  if (field.type === "lines") {
    const lines = asLines(value);
    const set = (next: LineItem[]) => onChange(next);
    return (
      <div className={cls}>
        <Field label={label} hint={field.hint}>
          <div className="lines">
            {lines.map((line, i) => (
              <div className="lines__row" key={i}>
                <Input
                  value={line.description}
                  placeholder="Description"
                  onChange={(e) =>
                    set(lines.map((l, j) => (j === i ? { ...l, description: e.target.value } : l)))
                  }
                />
                <Input
                  type="number"
                  value={String(line.amount ?? "")}
                  placeholder="Amount"
                  onChange={(e) =>
                    set(
                      lines.map((l, j) =>
                        j === i ? { ...l, amount: e.target.value === "" ? "" : Number(e.target.value) } : l,
                      ),
                    )
                  }
                />
                <button
                  type="button"
                  className="lines__del"
                  aria-label="Remove line"
                  onClick={() => set(lines.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              className="ax-btn ax-btn--outline ax-btn--sm"
              onClick={() => set([...lines, { description: "", amount: "" }])}
            >
              Add line
            </button>
          </div>
        </Field>
      </div>
    );
  }

  if (field.type === "table" && field.columns) {
    const columns = field.columns;
    const rows = asRows(value);
    const set = (next: TableRowValue[]) => onChange(next);
    const totals = totalsFor(columns, rows);
    return (
      <div className="ff ff--wide">
        <Field label={label} hint={field.hint}>
          <div className="tbl-field">
            <div className="tbl-field__scroll">
              <table className="tbl-field__t">
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th key={c.key} style={{ width: `${c.width * 9}%` }}>
                        {c.label}
                      </th>
                    ))}
                    <th aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      {columns.map((c) => (
                        <td key={c.key}>
                          <Input
                            type={c.type === "number" ? "number" : c.type === "time" ? "time" : "text"}
                            value={String(row[c.key] ?? "")}
                            onChange={(e) =>
                              set(
                                rows.map((r, j) =>
                                  j === i ? { ...r, [c.key]: e.target.value } : r,
                                ),
                              )
                            }
                          />
                        </td>
                      ))}
                      <td>
                        <button
                          type="button"
                          className="lines__del"
                          aria-label="Remove row"
                          onClick={() => set(rows.filter((_, j) => j !== i))}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totals.length > 0 && (
              <div className="tbl-field__totals">
                {totals.map((t) => (
                  <span key={t.label}>
                    {t.label}: <b>{t.total.toLocaleString()}</b>
                  </span>
                ))}
              </div>
            )}
            <button
              type="button"
              className="ax-btn ax-btn--outline ax-btn--sm"
              onClick={() => set([...rows, blankRow(columns)])}
            >
              Add row
            </button>
          </div>
        </Field>
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div className={cls}>
        <Field label={label} hint={field.hint}>
          <Select value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
            <option value="">—</option>
            {(field.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div className={cls}>
        <Field label={label} hint={field.hint}>
          <textarea
            className="ax-input ax-textarea"
            rows={3}
            value={String(value ?? "")}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      </div>
    );
  }

  const inputType =
    field.type === "date" ? "date"
    : field.type === "datetime" ? "datetime-local"
    : field.type === "number" || field.type === "money" ? "number"
    : "text";

  return (
    <div className={cls}>
      <Field label={label} hint={field.hint}>
        <Input
          type={inputType}
          value={String(value ?? "")}
          onChange={(e) =>
            onChange(
              field.type === "number" || field.type === "money"
                ? e.target.value === "" ? "" : Number(e.target.value)
                : e.target.value,
            )
          }
        />
      </Field>
    </div>
  );
}

export function FormFieldGrid({
  fields,
  values,
  onChange,
}: {
  fields: FormField[];
  values: FormValues;
  onChange: (key: string, v: unknown) => void;
}) {
  return (
    <div className="ff-grid">
      {fields.map((f) => (
        <FormFieldInput
          key={f.key}
          field={f}
          value={values[f.key]}
          onChange={(v) => onChange(f.key, v)}
        />
      ))}
    </div>
  );
}

/** Read-only rendering of a submitted form body. */
export function FormFieldReadout({
  fields,
  values,
}: {
  fields: FormField[];
  values: FormValues;
}) {
  const shown = fields.filter((f) => {
    const v = values[f.key];
    return v !== undefined && v !== "" && v !== null && !(Array.isArray(v) && v.length === 0);
  });
  if (shown.length === 0) return <p className="muted">No details recorded.</p>;

  return (
    <dl className="ff-read">
      {shown.map((f) => {
        const v = values[f.key];
        let text: string;
        if (f.type === "table" && f.columns) {
          const columns = f.columns;
          const rows = asRows(v);
          const totals = totalsFor(columns, rows);
          return (
            <div className="ff-read__row ff-read__row--wide" key={f.key}>
              <dt>{f.label}</dt>
              <dd>
                <div className="tbl-field__scroll">
                  <table className="tbl-read">
                    <thead>
                      <tr>
                        {columns.map((c) => (
                          <th key={c.key}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr key={i}>
                          {columns.map((c) => (
                            <td key={c.key}>{String(row[c.key] ?? "")}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totals.length > 0 && (
                  <div className="tbl-field__totals">
                    {totals.map((t) => (
                      <span key={t.label}>
                        {t.label}: <b>{t.total.toLocaleString()}</b>
                      </span>
                    ))}
                  </div>
                )}
              </dd>
            </div>
          );
        }
        if (f.type === "bool") text = v ? "Yes" : "No";
        else if (f.type === "lines") {
          const lines = asLines(v);
          const total = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
          text = `${lines.map((l) => `${l.description} — ${l.amount}`).join(" · ")}  (total ${total.toLocaleString()})`;
        } else if (f.type === "money") text = Number(v).toLocaleString();
        else text = String(v);
        return (
          <div className={f.span > 1 ? "ff-read__row ff-read__row--wide" : "ff-read__row"} key={f.key}>
            <dt>{f.label}</dt>
            <dd>{text}</dd>
          </div>
        );
      })}
    </dl>
  );
}

/** Names of required fields that are still empty. */
export function missingRequired(fields: FormField[], values: FormValues): string[] {
  return fields
    .filter((f) => {
      const v = values[f.key];
      if (!f.required) return false;
      // A required table needs a row with something in it. `String([])` is ""
      // but `String([{}])` is "[object Object]", so the old check passed an
      // empty table and failed a full one.
      if (f.type === "table" || f.type === "lines") {
        const rows = asRows(v);
        return !rows.some((r) => Object.values(r).some((x) => String(x ?? "").trim() !== ""));
      }
      return String(v ?? "").trim() === "";
    })
    .map((f) => f.label);
}
